import classNames from "classnames";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import styles from "./modalContext.module.css";

const baseZIndex = 1000;

type ModalContextType = {
  pushModal: (id: number, close: () => void) => void;
  popModal: (id: number) => void;
  getStackIndex: (id: number) => number;
};

const ModalContext = createContext<ModalContextType | null>(null);

let uniqueIdCounter = 0;

export const BackdropProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stack, setStack] = useState<{ modalId: number; onClose: () => void }[]>([]);

  const pushModal = useCallback((id: number, onClose: () => void) => {
    setStack((prev) => [...prev, { modalId: id, onClose }]);
  }, []);

  const popModal = useCallback((id: number) => {
    setStack((prev) => prev.filter((i) => i.modalId !== id));
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const modalInfo = stack[stack.length - 1];
        if (modalInfo) {
          popModal(modalInfo.modalId);
          modalInfo.onClose();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [stack.length, popModal]);

  const getStackIndex = (id: number) => {
    const idx = stack.findIndex((x) => x.modalId === id);
    return idx >= 0 ? idx : 0;
  };

  return (
    <ModalContext.Provider value={{ pushModal, popModal, getStackIndex }}>
      {children}
      <div
        className={classNames(styles.backdrop, { [styles.visible]: stack.length > 0 })}
        style={{
          zIndex: baseZIndex + stack.length,
        }}
      />
    </ModalContext.Provider>
  );
};

export const useModalLayer = (close: () => void) => {
  const context = useContext(ModalContext);
  if (!context) throw new Error("useModalLayer must be used within BackdropProvider");

  const { pushModal, popModal, getStackIndex } = context;
  const [modalId] = useState(() => ++uniqueIdCounter);

  useEffect(() => {
    pushModal(modalId, close);
    return () => {
      popModal(modalId);
    };
  }, [modalId, pushModal, popModal]);

  return { zIndex: getStackIndex(modalId) + baseZIndex };
};
